import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getI18n } from '@/lib/i18n';
import api, { formatApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Paperclip, Upload, Trash2, Download, FileText } from 'lucide-react';

function formatSize(n, t) {
  if (n < 1024) return `${n} ${t('att.size.b')}`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} ${t('att.size.kb')}`;
  return `${(n / 1024 / 1024).toFixed(1)} ${t('att.size.mb')}`;
}

/**
 * Управление вложениями тендера.
 * @param tenderId   — id тендера
 * @param canEdit    — разрешены ли загрузка/удаление (организатор-владелец / super-admin)
 */
export default function TenderAttachments({ tenderId, canEdit = false }) {
  const { user } = useAuth();
  const { t } = getI18n(user);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    if (!tenderId) return;
    try {
      const { data } = await api.get(`/tenders/${tenderId}/attachments`);
      setFiles(data || []);
    } catch {} finally { setLoading(false); }
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      return toast.error(t('att.too_big'));
    }
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post(`/tenders/${tenderId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('att.uploaded'));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const handleDownload = async (f) => {
    try {
      const resp = await api.get(`/tenders/${tenderId}/attachments/${f.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: f.content_type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleDelete = async (f) => {
    if (!window.confirm(t('att.confirm_delete').replace('{name}', f.filename))) return;
    try {
      await api.delete(`/tenders/${tenderId}/attachments/${f.id}`);
      toast.success(t('att.deleted'));
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`attachments-${tenderId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-600">
          <Paperclip className="w-3.5 h-3.5" /> {t('att.title')}
          <span className="text-[10px] text-slate-400 font-normal">({files.length})</span>
        </div>
        {canEdit && (
          <label className="inline-flex items-center gap-1.5 text-xs text-[#0046AD] cursor-pointer hover:underline"
            data-testid={`attach-upload-${tenderId}`}>
            <Upload className="w-3 h-3" /> {uploading ? t('att.uploading') : t('att.upload')}
            <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} />
          </label>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-slate-400">{t('att.loading')}</div>
      ) : files.length === 0 ? (
        <div className="text-xs text-slate-400">{t('att.empty')}</div>
      ) : (
        <div className="space-y-1">
          {files.map(f => (
            <div key={f.id} className="flex items-center gap-2 px-2 py-1 rounded-sm bg-slate-50 border border-slate-100 text-xs">
              <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <button onClick={() => handleDownload(f)} data-testid={`attach-dl-${f.id}`}
                className="flex-1 min-w-0 text-left text-slate-700 hover:text-[#0046AD] truncate">
                {f.filename}
              </button>
              <span className="text-[10px] text-slate-400 shrink-0">{formatSize(f.size, t)}</span>
              <Button size="sm" variant="ghost" onClick={() => handleDownload(f)}
                className="h-6 w-6 p-0 rounded-sm text-slate-500 hover:text-[#0046AD]">
                <Download className="w-3 h-3" />
              </Button>
              {canEdit && (
                <Button size="sm" variant="ghost" onClick={() => handleDelete(f)}
                  data-testid={`attach-del-${f.id}`}
                  className="h-6 w-6 p-0 rounded-sm text-slate-500 hover:text-rose-600">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
